import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, SlidersHorizontalIcon } from 'lucide-react';
import { CalculatorSlider } from '../CalculatorSlider';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
function formatEuro(value: number) {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(value);
}
export function SlideCampaignCostCalculator({}: SlideProps) {
  const [brands, setBrands] = useState(1);
  const [campaignsPerYear, setCampaignsPerYear] = useState(4);
  const [countries, setCountries] = useState(1);
  const [economicsOpen, setEconomicsOpen] = useState(false);
  const calculation = useMemo(() => {
    // List prices before any volume discount.
    const masterBase = 2900; // one master build per brand, per year
    const versioningBase = 500; // every campaign after the first is a version of that master
    const localizationBase = 999;
    const monthlyInsightsBase = 99;
    const campaignDuration = 3;

    // Volume curves: little is given away at low volume, and the discount
    // accelerates as commitment grows (exponent > 1 = back-loaded).
    const scale = (value: number, min: number, max: number, curve: number) => Math.pow(Math.min(1, Math.max(0, (value - min) / (max - min))), curve);
    const brandScale = scale(brands, 1, 40, 1.35);
    const frequencyScale = scale(campaignsPerYear, 1, 12, 2);
    const countryScale = scale(countries, 1, 100, 1);

    // Portfolio breadth and campaign frequency amortize the master build;
    // market count and frequency amortize localization and monitoring.
    const masterCost = masterBase * (1 - 0.3 * brandScale - 0.3 * frequencyScale);
    const versioningCost = versioningBase * (1 - 0.4 * frequencyScale - 0.2 * brandScale);
    const localizationCost = localizationBase * Math.max(0.28, 1 - 0.58 * countryScale - 0.28 * brandScale - 0.25 * frequencyScale);
    const monthlyInsights = monthlyInsightsBase * Math.max(0.3, 1 - 0.45 * countryScale - 0.28 * frequencyScale - 0.2 * brandScale);
    const annualCampaigns = brands * campaignsPerYear;
    const perCountryActivation = localizationCost + monthlyInsights * campaignDuration;
    const firstCampaign = masterCost + perCountryActivation * countries;
    const followUpCampaign = versioningCost + perCountryActivation * countries;
    const totalDeployments = annualCampaigns * countries;
    const totalAnnualCost = brands * firstCampaign + Math.max(0, annualCampaigns - brands) * followUpCampaign;
    return {
      masterCost,
      versioningCost,
      localizationCost,
      monthlyInsights,
      annualCampaigns,
      totalDeployments,
      costPerDeployment: totalDeployments ? totalAnnualCost / totalDeployments : 0
    };
  }, [brands, campaignsPerYear, countries]);
  return <InteriorSlide surface data-id="element-246" data-mp="3b0sbkwyi1aeh">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(1rem,1.8vw,1.6rem)] flex flex-shrink-0 items-end justify-between gap-8" data-id="element-247" data-mp="3b23e6tg7kc40">
        <div data-id="element-248" data-mp="2wbw4b249o1ex">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-249" data-mp="2wce1q22o0lof">
            Portfolio economics
          </p>
          <h2 className="max-w-4xl text-[clamp(2rem,3.4vw,3.25rem)] font-extrabold leading-[1.03] tracking-[-0.04em] text-[var(--fg)]" data-id="element-250" data-mp="3p0a6zv6avlwt">
            See for yourself—move the sliders to see{' '}
            <span className="text-[var(--brand)]" data-id="element-251" data-mp="3k31nnr9f1btz">your potential.</span>
          </h2>
        </div>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-252" data-mp="28ls1gwywk5oz">
        <motion.section initial={{
        opacity: 0,
        x: -18
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.15,
        duration: 0.55
      }} className="flex min-h-0 flex-col gap-4 lg:col-span-5" aria-label="Portfolio calculator inputs" data-id="element-253" data-mp="28jp7k1umpsez">
          <article className="rounded-3xl border border-[var(--brand)]/10 bg-white p-5 shadow-sm" data-id="element-254" data-mp="14a2d50qkeq2">
            <div className="mb-5 flex items-center gap-3" data-id="element-255" data-mp="3ih4118rjms24">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-256" data-mp="3nitawl314zom">
                <SlidersHorizontalIcon className="h-5 w-5" aria-hidden="true" data-id="element-257" data-mp="f05fsvcd8xm2" />
              </span>
              <div data-id="element-258" data-mp="jz8fs1dhodn6">
                <h3 className="font-extrabold text-[var(--fg)]" data-id="element-259" data-mp="61tp6tbi1ztm">Portfolio scale</h3>
                <p className="text-xs text-[var(--muted)]" data-id="element-260" data-mp="63wax15md6sj">Revenue updates instantly.</p>
              </div>
            </div>
            <div className="space-y-5" data-id="element-261" data-mp="2qr9chebif6ei">
              <CalculatorSlider label="Number of Countries" value={countries} min={1} max={130} onChange={setCountries} data-id="element-262" data-mp="2qq7oyadb4rfw" />
              <CalculatorSlider label="Frequency of campaigns annually" value={campaignsPerYear} min={1} max={12} onChange={setCampaignsPerYear} data-id="element-263" data-mp="2ur9pcuiyn9jg" />
              <CalculatorSlider label="Number of Brands" value={brands} min={1} max={40} onChange={setBrands} data-id="element-264" data-mp="2ibjpw1jp2meo" />
            </div>
          </article>

          <article className="overflow-hidden rounded-2xl border border-[var(--brand)]/10 bg-white" data-id="element-265" data-mp="2x5ycsd6dqfym">
            <button type="button" onClick={() => setEconomicsOpen(open => !open)} aria-expanded={economicsOpen} className="flex w-full items-center gap-3 px-5 py-3 text-left transition hover:bg-[var(--brand)]/[0.03]" data-id="element-266" data-mp="2s6vb92plyrve">
              <div className="h-5 w-5 text-[var(--brand)]" aria-hidden="true" data-id="element-267" data-mp="2e96pztrmpipt" />
              <span className="flex-1" data-id="element-268" data-mp="2eb9jinrzptza">
                <span className="block text-sm font-bold text-[var(--fg)]" data-id="element-269" data-mp="29i6qcgr4kmjm">Unit economics</span>
                <span className="mt-0.5 block text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--muted)]" data-id="element-270" data-mp="1lsci6chr3m5p">Discounted for scale</span>
              </span>
              <ChevronDownIcon className={`h-4 w-4 text-[var(--muted)] transition ${economicsOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-271" data-mp="1gryf78v0b2ej" />
            </button>
            <AnimatePresence initial={false} data-id="element-272" data-mp="2ai77qzfgpvfp">
              {economicsOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="overflow-hidden" data-id="element-273" data-mp="2f9qf70bwjzm7">
                  <div className="grid grid-cols-4 gap-2 border-t border-[var(--brand)]/10 bg-[var(--brand)]/[0.03] p-3" data-id="element-274" data-mp="1rke57y8r3niu">
                    <Rate label="Master Snak" value={formatEuro(calculation.masterCost)} data-id="element-275" data-mp="1wjha9ioqdtqt" />
                    <Rate label="Campaign version" value={formatEuro(calculation.versioningCost)} data-id="element-276" data-mp="21j3qm0zuvq3u" />
                    <Rate label="Localization" value={formatEuro(calculation.localizationCost)} data-id="element-277" data-mp="37zp6z3hheauk" />
                    <Rate label="Insights / month" value={formatEuro(calculation.monthlyInsights)} data-id="element-278" data-mp="3cx7j12qsp66s" />
                  </div>
                </motion.div>}
            </AnimatePresence>
          </article>

          <p className="px-1 text-[11px] italic leading-snug text-[var(--muted)]" data-id="element-279" data-mp="wbakxszhkac6">
            Indicative scaled rates for discussion — a shared starting point,
            not a binding offer. Final pricing is agreed per scope.
          </p>
        </motion.section>

        <motion.section initial={{
        opacity: 0,
        x: 18
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.27,
        duration: 0.55
      }} className="flex min-h-0 flex-col gap-4 lg:col-span-7" aria-label="Portfolio calculator results" data-id="element-280" data-mp="ik5the69uocx">
          <article className="relative overflow-hidden rounded-3xl bg-[var(--brand)] p-[clamp(1.5rem,2.5vw,2.5rem)] text-white shadow-lg" data-id="element-281" data-mp="8nszef6te8hn">
            <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full border-[34px] border-white/10" data-id="element-282" data-mp="23090z2oo0yg6" />
            <p className="relative text-xs font-bold uppercase tracking-[0.16em] text-white/65" data-id="element-283" data-mp="1y27lssud1j9z">
              Cost per localized campaign
            </p>
            <motion.p key={calculation.costPerDeployment} initial={{
            opacity: 0.6,
            y: 4
          }} animate={{
            opacity: 1,
            y: 0
          }} className="relative mt-3 text-[clamp(3.25rem,7vw,6.5rem)] font-extrabold leading-none tracking-[-0.06em]" data-id="element-284" data-mp="1bun87k9s04hn">
              {formatEuro(calculation.costPerDeployment)}
            </motion.p>
            <p className="relative mt-3 text-sm text-white/70" data-id="element-285" data-mp="o75g1nmln4e5">Per localized deployment</p>
          </article>

          <div className="grid grid-cols-2 gap-4" data-id="element-286" data-mp="t1svkwzmkogl">
            <ResultCard value={calculation.annualCampaigns.toLocaleString()} label="Master campaigns / year" data-id="element-287" data-mp="f5pf06hoceml" />
            <ResultCard value={calculation.totalDeployments.toLocaleString()} label="Localized deployments / year" accent data-id="element-288" data-mp="j6ho8zqjqjiv" />
          </div>

          <article className="flex flex-1 items-center rounded-3xl border border-[var(--brand)]/10 bg-white p-5 shadow-sm" data-id="element-289" data-mp="2dk0gbu1t59ws">
            <p className="text-sm leading-relaxed text-[var(--muted)]" data-id="element-290" data-mp="1w5b1wxq7rqu">
              Entrust Snakker with a portfolio of brands across selected markets to prove the concept before expanding—we’re working toward a pricing model based on actual impact.
            </p>
          </article>
        </motion.section>
      </div>
    </InteriorSlide>;
}
function Rate({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="rounded-xl bg-white p-3 text-center" data-id="element-291" data-mp="ljbdi2doo1c0">
      <p className="text-lg font-extrabold text-[var(--brand)]" data-id="element-292" data-mp="glteb3pgx0h7">{value}</p>
      <p className="mt-1 text-[9px] font-bold uppercase tracking-wide text-[var(--muted)]" data-id="element-293" data-mp="gnw3jlisqpkv">{label}</p>
    </div>;
}
function ResultCard({
  value,
  label,
  accent = false
}: {
  value: string;
  label: string;
  accent?: boolean;
}) {
  return <article className="rounded-2xl border border-[var(--brand)]/10 bg-white p-5 shadow-sm" data-id="element-294" data-mp="2wbw5segmppnd">
      <p className={`text-3xl font-extrabold ${accent ? 'text-[var(--brand)]' : 'text-[var(--fg)]'}`} data-id="element-295" data-mp="2re4gztba6jne">{value}</p>
      <p className="mt-2 text-xs font-semibold text-[var(--muted)]" data-id="element-296" data-mp="2h3a0esg8gjnz">{label}</p>
    </article>;
}